/* eslint-disable no-restricted-imports */
import { redirect, RouteComponent, createFileRoute as tanstackCreateFileRoute } from '@tanstack/react-router';
/* eslint-enable no-restricted-imports */
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { authnClient } from '@frontend/auth';
import { hasSchemaACL } from '@frontend/auth-helpers';

type Routes = Parameters<typeof tanstackCreateFileRoute>[0];

/**
 * Parameters for creating a route in WAM2.
 */
interface CreateRouteParams {
  /**
   * The file-system path for the route that corresponds to the file location in the routes directory.
   *
   * IMPORTANT: This must EXACTLY match your file path structure:
   * - File: `routes/dashboard.tsx` → path: `'/dashboard'`
   * - File: `routes/settings/users.tsx` → path: `'/settings/users'`
   * - File: `routes/phone/call-settings.tsx` → path: `'/phone/call-settings'`
   *
   * TanStack Router uses file-based routing, so the path parameter must match your file structure.
   * Leading slash is required. Do not include trailing slashes unless it's the root route.
   *
   * @example '/dashboard' - for routes/dashboard.tsx
   * @example '/settings/users' - for routes/settings/users.tsx
   * @example '/phone/call-settings' - for routes/phone/call-settings.tsx
   * @example '/' - for routes/index.tsx (root route)
   */
  path: Routes;

  /**
   * The React component that will be rendered when this route is active.
   *
   * This should be a functional or class component that serves as the main content for the route.
   * The component will receive route context and props from TanStack Router.
   *
   * Best practices:
   * - Define your component in the same file above the Route export
   * - Use PascalCase naming (e.g., DashboardPage, UserSettings)
   * - Keep route components focused on layout and data fetching
   * - Delegate complex UI to child components
   *
   * @example
   * function DashboardPage() {
   *   return <Page title="Dashboard">...</Page>;
   * }
   *
   * export const Route = createFileRoute({
   *   path: '/dashboard',
   *   component: DashboardPage,
   *   type: 'auth',
   * });
   */
  component: RouteComponent;

  /**
   * Defines the authentication requirements for this route.
   *
   * 'auth' - PROTECTED ROUTE (most common):
   *   - Requires user to be authenticated via Okta
   *   - Unauthenticated users are automatically redirected to /sign-in
   *   - Original URL is preserved in search params for post-login redirect
   *   - Use for: Dashboard, settings, phone management, user data, etc.
   *
   * 'public' - PUBLIC ROUTE:
   *   - Accessible without authentication
   *   - No authentication checks are performed
   *   - Use for: Sign-in page, sign-out, public landing pages
   *
   * DECISION GUIDE:
   * - Does this page show user/location-specific data? → Use 'auth'
   * - Is this page part of the authenticated app experience? → Use 'auth'
   * - Is this the sign-in, sign-out, or a public page? → Use 'public'
   * - When in doubt, use 'auth' (most routes are protected)
   *
   * @example
   * // Protected route requiring authentication
   * type: 'auth'
   *
   * @example
   * // Public route (sign-in, sign-out only)
   * type: 'public'
   */
  type: 'auth' | 'public';

  /**
   * Determines if a location must be selected before accessing this route.
   *
   * WHAT IT DOES:
   * When set to true:
   *   1. Checks if user has selected a location (checks localStorage for location history)
   *   2. If NO location found:
   *      - User is redirected to /location (location picker page)
   *      - Original destination is saved: /location?redirect=/your-route
   *   3. After location selection:
   *      - User is automatically redirected back to the original route
   *   4. If location exists:
   *      - Route loads normally
   *      - Location service provides currentLocation to your component
   *
   * WHEN TO USE TRUE:
   * ✅ Route displays data specific to a practice location:
   *    - Patient lists, phone settings, office hours
   *    - Location-specific dashboards, reports, analytics
   *    - Settings that vary by location
   * ✅ Route performs actions scoped to a location:
   *    - Ordering phone numbers, managing devices
   *    - Location-specific configurations
   *
   * WHEN TO USE FALSE (or omit):
   * ❌ Global/multi-location routes:
   *    - Global settings, feature flags, system configs
   *    - Routes that work across all locations
   * ❌ System routes:
   *    - The /location picker page itself (infinite loop!)
   *    - Sign-in, sign-out, authentication flows
   *    - About page, help documentation
   * ❌ Routes that let users SELECT a location:
   *    - Multi-location dashboards where user picks after page loads
   *
   * TECHNICAL DETAILS:
   * - Location history key: 'wam-location-service__location-history'
   * - Redirect happens in route's beforeLoad guard (runs before render)
   * - Location service automatically loads last visited location
   * - Uses localStorage to persist location history across sessions
   *
   * @default false
   *
   * @example
   * // Location-specific route: phone call settings for a practice location
   * export const Route = createFileRoute({
   *   path: '/phone/call-settings',
   *   component: CallSettingsPage,
   *   type: 'auth',
   *   locationRequired: true, // ✅ Requires location
   * });
   *
   * @example
   * // Global route: system-wide feature flags
   * export const Route = createFileRoute({
   *   path: '/global-settings/feature-flags',
   *   component: FeatureFlagsPage,
   *   type: 'auth',
   *   locationRequired: false, // ❌ No location needed
   * });
   *
   * @example
   * // Default false: can be omitted if not needed
   * export const Route = createFileRoute({
   *   path: '/about',
   *   component: AboutPage,
   *   type: 'auth',
   *   // locationRequired omitted, defaults to false
   * });
   */
  locationRequired?: boolean;

  /**
   * An array of ACL (Access Control List) numbers associated with this route.
   *
   * WHAT IT DOES:
   * These ACL values are passed through the route context so that the page component
   * can access them at render time. This enables the component to conditionally show
   * or hide UI elements based on the user's permissions.
   *
   * HOW TO ACCESS IN YOUR COMPONENT:
   * The `acls` array is returned from `beforeLoad` and merged into the route context.
   * Access it in your component via `Route.useRouteContext()`:
   *
   * ```tsx
   * const { acls } = Route.useRouteContext();
   * ```
   *
   * IMPORTANT:
   * - This is always an array — either empty `[]` or populated with ACL numbers.
   * - These ACLs are NOT enforced by the route middleware. They are informational
   *   and passed through for the component to use. To enforce an ACL as a gate,
   *   use `requiredAcl` instead.
   * - ACL values correspond to the `Permission` enum from `@weave/schema-gen-ts/dist/shared/waccess/acls.pb`.
   * - Only available on `type: 'auth'` routes (public routes do not have route context).
   *
   * @default []
   *
   * @example
   * // Route that passes ACLs for the component to use
   * import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
   *
   * export const Route = createFileRoute({
   *   path: '/phone/call-settings',
   *   component: CallSettingsPage,
   *   type: 'auth',
   *   locationRequired: true,
   *   acls: [Permission.CALL_RECORDING_SETTING_WRITE, Permission.CALL_FORWARDING_SETTING_WRITE, Permission.OFFICE_HOURS_SETTING_WRITE],
   * });
   *
   * @example
   * // No ACLs needed — defaults to empty array
   * export const Route = createFileRoute({
   *   path: '/about',
   *   component: AboutPage,
   *   type: 'auth',
   *   acls: [],
   * });
   */
  acls?: Permission[];

  /**
   * An array of ACL Permissions that act as a gate for accessing this route.
   *
   * WHAT IT DOES:
   * When provided, the route middleware checks if the current user has ALL of these ACLs
   * for their selected location. If the user does NOT have all the required ACLs,
   * they are redirected to `/access-denied`.
   *
   * HOW IT WORKS:
   *   1. After authentication and location checks pass, the middleware reads the
   *      current location ID from localStorage (location history).
   *   2. Calls `hasSchemaACL(locationId, requiredAcl)` from `@frontend/auth-helpers`
   *      to verify the user has all the permissions.
   *   3. For Weave internal users, ACLs are checked against the 'weave' scope
   *      (location ID is not required).
   *   4. For external users, ACLs are checked against the selected location.
   *   5. If the check fails, the user is redirected to `/access-denied` with
   *      the original URL preserved in search params.
   *
   * WHEN TO USE:
   * ✅ Routes that should only be accessible to users with specific permissions:
   *    - Admin pages requiring `[Permission.USER_WRITE]`
   *    - Feature flag management requiring `[Permission.FEATURE_FLAG_WRITE]`
   *    - Desktop releases requiring `[Permission.APP_RELEASE_WRITE]`
   * ✅ Routes where unauthorized users should be blocked entirely
   *
   * WHEN NOT TO USE:
   * ❌ When you only need to conditionally show/hide UI elements (use `acls` instead)
   * ❌ Public routes — ACL checks require authentication
   *
   * IMPORTANT:
   * - Values come from the `Permission` enum in `@weave/schema-gen-ts/dist/shared/waccess/acls.pb`.
   * - The check runs in `beforeLoad`, so the component never renders if the user lacks permission.
   * - If no location is in history and the user is not a Weave internal user,
   *   the ACL check will fail and the user will be redirected.
   *
   * @example
   * // Only users with APP_RELEASE_WRITE can access this route
   * import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
   *
   * export const Route = createFileRoute({
   *   path: '/global-settings/desktop-releases',
   *   component: DesktopReleasesPage,
   *   type: 'auth',
   *   requiredAcl: [Permission.APP_RELEASE_WRITE],
   * });
   *
   * @example
   * // Combine with acls for gate + informational permissions
   * import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
   *
   * export const Route = createFileRoute({
   *   path: '/phone/call-settings',
   *   component: CallSettingsPage,
   *   type: 'auth',
   *   locationRequired: true,
   *   requiredAcl: [Permission.CALL_RECORDING_SETTING_WRITE],
   *   acls: [Permission.CALL_RECORDING_SETTING_WRITE, Permission.CALL_FORWARDING_SETTING_WRITE, Permission.OFFICE_HOURS_SETTING_WRITE],
   * });
   */
  requiredAcl?: Permission[];
}

const LOCATION_HISTORY_KEY = 'wam-location-service__location-history';

/**
 * Safely parses a JSON string, returning a fallback value if parsing fails.
 */
function safeParseJson<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

/**
 * Creates a file-based route with authentication and location requirements.
 *
 * This factory function wraps TanStack Router's createFileRoute with additional
 * middleware for authentication checks and location requirements.
 *
 * @param params - The route configuration parameters
 * @param params.path - The route path matching the file location (e.g., '/dashboard', '/settings/users')
 * @param params.component - The React component to render for this route
 * @param params.type - Authentication requirement: 'auth' (requires login) or 'public' (no auth needed)
 * @param params.locationRequired - Whether a location must be selected before accessing this route (default: false)
 * @param params.acls - Array of ACL numbers to pass through route context for the component to use (default: [])
 * @param params.requiredAcl - Array of ACL permissions that gate route access — user is redirected to /access-denied if they lack any of these permissions
 *
 * @returns A configured TanStack Router route
 *
 * @example
 * // Authenticated route that requires a location
 * export const Route = createFileRoute({
 *   path: '/dashboard',
 *   component: DashboardPage,
 *   type: 'auth',
 *   locationRequired: true,
 * });
 *
 * @example
 * // Public route (no auth or location required)
 * export const Route = createFileRoute({
 *   path: '/sign-in',
 *   component: SignInPage,
 *   type: 'public',
 * });
 *
 * @example
 * // Authenticated route without location requirement
 * export const Route = createFileRoute({
 *   path: '/global-settings',
 *   component: GlobalSettingsPage,
 *   type: 'auth',
 * });
 */
export const createFileRoute = ({
  path,
  component,
  type,
  locationRequired = false,
  acls = [],
  requiredAcl,
}: CreateRouteParams) => {
  if (type === 'auth') {
    return tanstackCreateFileRoute(path)({
      component,
      beforeLoad: async ({ location }) => {
        if (!authnClient.isUserAuthenticated()) {
          throw redirect({
            to: '/sign-in',
            search: {
              redirect: location.href,
            },
          } as any);
        }

        /* Check if location is required and not yet selected */
        if (locationRequired) {
          /* Check if user has any location history in localStorage
          The location service automatically loads the last visited location */
          const locationHistory = safeParseJson<any[]>(localStorage.getItem(LOCATION_HISTORY_KEY), []);
          const hasLocationHistory = locationHistory.length > 0;

          if (!hasLocationHistory) {
            throw redirect({
              to: '/location',
              search: {
                redirect: location.href,
              },
            } as any);
          }
        }

        /* Check if user has the required ACL to access this route.
         * The requiredAcl acts as a gate — if the user does not have this ACL,
         * they are redirected to /access-denied.
         * For Weave internal users, ACLs are checked against the 'weave' scope.
         * For external users, ACLs are checked against the selected location. */
        if (requiredAcl !== undefined && requiredAcl.length > 0) {
          const locationHistory = safeParseJson<any[]>(localStorage.getItem(LOCATION_HISTORY_KEY), []);
          const currentLocationId = locationHistory[0]?.locationId ?? '';

          if (!hasSchemaACL(currentLocationId, requiredAcl)) {
            throw redirect({
              to: '/access-denied',
              search: {
                redirect: location.href,
                requiredAcl: requiredAcl.map(String).join(','),
                acls: acls.map(String).join(','),
              },
            } as any);
          }
        }

        /* Return ACLs in route context so components can access them
         * via Route.useRouteContext() */
        return { acls };
      },
    });
  } else {
    return tanstackCreateFileRoute(path)({
      component,
    });
  }
};
